<template>
  <div :class="$style.container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <div :class="$style.header">
      <h1 :class="$style.title">🏢 장소별 통계</h1>
      <p :class="$style.subtitle">
        등록된 장소별 통계 정보를 확인할 수 있습니다.
      </p>
    </div>

    <!-- 요약 카드 -->
    <div :class="$style.summary">
      <div :class="$style.summaryCard">
        <h3>전체 장소</h3>
        <p :class="$style.summaryNumber">{{ totalVenues }}</p>
      </div>
      <div :class="$style.summaryCard">
        <h3>활성 장소</h3>
        <p :class="$style.summaryNumber">{{ activeVenues }}</p>
      </div>
      <div :class="$style.summaryCard">
        <h3>총 공연 수</h3>
        <p :class="$style.summaryNumber">{{ totalLives }}</p>
      </div>
    </div>

    <!-- 검색 및 필터 -->
    <div :class="$style.controls">
      <div :class="$style.searchBox">
        <input v-model="searchQuery" type="text" placeholder="장소명으로 검색..." :class="$style.searchInput" />
      </div>
      <div :class="$style.sortBox">
        <select v-model="sortBy" :class="$style.sortSelect">
          <option value="name">장소명순</option>
          <option value="lives">공연수순</option>
          <option value="firstAppearance">첫 등록순</option>
          <option value="lastAppearance">최근 등록순</option>
        </select>
      </div>
    </div>

    <!-- 공연장 목록 -->
    <div v-if="loading" :class="$style.loading">
      <div :class="$style.loadingContent">
        <div :class="$style.spinner"></div>
        <p>장소별 통계를 불러오는 중...</p>
      </div>
    </div>

    <div v-else-if="venues.length === 0" :class="$style.noData">
      <p>등록된 공연장이 없습니다.</p>
    </div>

    <div v-else :class="$style.venueList">
      <div v-for="(venue, index) in paginatedVenues" :key="venue.name" :class="$style.venueCard">
        <div :class="$style.venueHeader">
          <div :class="$style.venueRank">
            <span :class="$style.rankNumber">{{ getRank(index) }}</span>
          </div>

          <!-- 공연장명 표시/수정 -->
          <div v-if="!venue.editingName" :class="$style.venueNameDisplay">
            <h3 :class="$style.venueName">{{ venue.name }}</h3>
            <button @click="startEditName(venue)" :class="$style.editNameButton">
              ✏️
            </button>
          </div>

          <!-- 공연장명 수정 모드 -->
          <div v-else :class="$style.venueNameEdit">
            <input v-model="venue.tempName" type="text" placeholder="공연장명 입력" :class="$style.venueNameInput" />
            <div :class="$style.venueNameActions">
              <button @click="saveVenueName(venue)" :class="$style.saveButton"
                :disabled="venue.savingName || !venue.tempName || venue.tempName === venue.name">
                {{ venue.savingName ? "저장 중..." : "저장" }}
              </button>
              <button @click="cancelEditName(venue)" :class="$style.cancelButton">
                취소
              </button>
            </div>
          </div>

          <span :class="$style.venueCount">{{ venue.lives.length }}개 공연</span>
        </div>

        <div :class="$style.venueInfo">
          <div :class="$style.infoItem">
            <strong>보컬:</strong> {{ getTypeCount(venue, "vocal") }}개
          </div>
          <div :class="$style.infoItem">
            <strong>밴드:</strong> {{ getTypeCount(venue, "band") }}개
          </div>
          <div :class="$style.infoItem">
            <strong>댄스:</strong> {{ getTypeCount(venue, "dance") }}개
          </div>
          <div :class="$style.infoItem">
            <strong>믹스:</strong> {{ getTypeCount(venue, "mix") }}개
          </div>
        </div>

        <div :class="$style.liveList">
          <span v-for="live in venue.lives" :key="live.id" :class="$style.liveTag" @click="goToLiveDetail(live.id)"
            style="cursor: pointer">
            <img :src="typeIconUrl(live.type)" :alt="live.type" :class="$style.typeIcon" />
            {{ live.title }}
          </span>
        </div>

        <div :class="$style.mapLinkSection">
          <!-- 저장되지 않은 경우: 입력 폼 -->
          <div v-if="!venue.mapLink" :class="$style.mapLinkInput">
            <input v-model="venue.tempMapLink" type="text" placeholder="지도 링크를 입력하세요 (예: https://maps.google.com/...)"
              :class="$style.mapInput" />
            <button @click="saveMapLink(venue)" :class="$style.saveButton"
              :disabled="venue.saving || !venue.tempMapLink">
              {{ venue.saving ? "저장 중..." : "저장" }}
            </button>
          </div>

          <!-- 저장된 경우: 텍스트 표시 및 수정 버튼 -->
          <div v-else-if="!venue.editing" :class="$style.mapLinkDisplay">
            <div :class="$style.mapLinkText">
              <a :href="venue.mapLink" target="_blank" :class="$style.mapLink">
                {{ venue.mapLink }}
              </a>
            </div>
            <div :class="$style.mapLinkActions">
              <button @click="startEdit(venue)" :class="$style.editButton">
                수정
              </button>
            </div>
          </div>

          <!-- 수정 모드: 수정된 링크 입력 -->
          <div v-else :class="$style.mapLinkInput">
            <input v-model="venue.tempMapLink" type="text" placeholder="지도 링크를 입력하세요 (예: https://maps.google.com/...)"
              :class="$style.mapInput" />
            <div :class="$style.editActions">
              <button @click="saveMapLink(venue)" :class="$style.saveButton"
                :disabled="venue.saving || !venue.tempMapLink">
                {{ venue.saving ? "저장 중..." : "저장" }}
              </button>
              <button @click="cancelEdit(venue)" :class="$style.cancelButton">
                취소
              </button>
            </div>
          </div>
        </div>

        <!-- 공연장 이미지 섹션 -->
        <VenueImageUploader :venue-name="venue.name" :venue-image="venue.venueImage" :show-controls="true"
          @image-updated="(url) => handleImageUpdated(venue, url)" @image-removed="() => handleImageRemoved(venue)" />

        <!-- 공연장 연락처 섹션 -->
        <div :class="$style.contactSection">
          <!-- 저장되지 않은 경우: 입력 폼 -->
          <div v-if="!venue.contact" :class="$style.contactInput">
            <input v-model="venue.tempContact" type="text" placeholder="연락처를 입력하세요 (예: 02-1234-5678)"
              :class="$style.contactInputField" />
            <button @click="saveContact(venue)" :class="$style.saveButton"
              :disabled="venue.savingContact || !venue.tempContact">
              {{ venue.savingContact ? "저장 중..." : "저장" }}
            </button>
          </div>

          <!-- 저장된 경우: 텍스트 표시 및 수정 버튼 -->
          <div v-else-if="!venue.editingContact" :class="$style.contactDisplay">
            <div :class="$style.contactText">
              <strong>연락처:</strong>
              <a :href="`tel:${venue.contact}`" :class="$style.contactLink">
                {{ venue.contact }}
              </a>
            </div>
            <div :class="$style.contactActions">
              <button @click="startEditContact(venue)" :class="$style.editButton">
                수정
              </button>
            </div>
          </div>

          <!-- 수정 모드: 수정된 연락처 입력 -->
          <div v-else :class="$style.contactInput">
            <input v-model="venue.tempContact" type="text" placeholder="연락처를 입력하세요 (예: 02-1234-5678)"
              :class="$style.contactInputField" />
            <div :class="$style.editActions">
              <button @click="saveContact(venue)" :class="$style.saveButton"
                :disabled="venue.savingContact || !venue.tempContact">
                {{ venue.savingContact ? "저장 중..." : "저장" }}
              </button>
              <button @click="cancelEditContact(venue)" :class="$style.cancelButton">
                취소
              </button>
            </div>
          </div>
        </div>

        <!-- 공연장 지역 섹션 -->
        <div :class="$style.regionSection">
          <!-- 저장되지 않은 경우: 입력 폼 -->
          <div v-if="!venue.region" :class="$style.regionInput">
            <input v-model="venue.tempRegion" type="text" placeholder="지역을 입력하세요 (예: 서울 홍대, 부산 서면)"
              :class="$style.regionInputField" />
            <button @click="saveRegion(venue)" :class="$style.saveButton"
              :disabled="venue.savingRegion || !venue.tempRegion">
              {{ venue.savingRegion ? "저장 중..." : "저장" }}
            </button>
          </div>

          <!-- 저장된 경우: 텍스트 표시 및 수정 버튼 -->
          <div v-else-if="!venue.editingRegion" :class="$style.regionDisplay">
            <div :class="$style.regionText">
              <strong>지역:</strong>
              <span :class="$style.regionValue">{{ venue.region }}</span>
            </div>
            <div :class="$style.regionActions">
              <button @click="startEditRegion(venue)" :class="$style.editButton">
                수정
              </button>
            </div>
          </div>

          <!-- 수정 모드: 수정된 지역 입력 -->
          <div v-else :class="$style.regionInput">
            <input v-model="venue.tempRegion" type="text" placeholder="지역을 입력하세요 (예: 서울 홍대, 부산 서면)"
              :class="$style.regionInputField" />
            <div :class="$style.editActions">
              <button @click="saveRegion(venue)" :class="$style.saveButton"
                :disabled="venue.savingRegion || !venue.tempRegion">
                {{ venue.savingRegion ? "저장 중..." : "저장" }}
              </button>
              <button @click="cancelEditRegion(venue)" :class="$style.cancelButton">
                취소
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <div :class="$style.pagination">
      <button :disabled="currentPage === 1" @click="currentPage--" :class="$style.pageButton">
        이전
      </button>
      <span :class="$style.pageInfo">
        {{ currentPage }} / {{ totalPages }}
      </span>
      <button :disabled="currentPage === totalPages" @click="currentPage++" :class="$style.pageButton">
        다음
      </button>
    </div>
  </div>
</template>

<script>
import { useUserStore } from "@/store/user";
import {
  getFirestore,
  collection,
  getDocs,
} from "firebase/firestore";
import {
  saveVenueMapLink,
  saveVenueContact,
  saveVenueRegion,
  renameVenue,
} from "@/utils/write/venue";
import {
  getStorage,
  ref as storageRef,
} from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { format } from "date-fns";
import VenueImageUploader from "@/components/VenueImageUploader.vue";
import StatsTabs from "@/components/StatsTabs.vue";

export default {
  name: "VenueStats",
  components: {
    VenueImageUploader,
    StatsTabs,
  },
  data() {
    return {
      userStore: useUserStore(),
      venues: [],
      loading: true,
      searchQuery: "",
      sortBy: "lives",
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    filteredVenues() {
      return this.venues.filter((venue) =>
        venue.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    sortedVenues() {
      const sorted = [...this.filteredVenues];

      switch (this.sortBy) {
        case "name":
          return sorted.sort((a, b) => a.name.localeCompare(b.name));
        case "lives":
          return sorted.sort((a, b) => b.lives.length - a.lives.length);
        case "firstAppearance":
          return sorted.sort((a, b) => a.firstAppearance - b.firstAppearance);
        case "lastAppearance":
          return sorted.sort((a, b) => b.lastAppearance - a.lastAppearance);
        default:
          return sorted;
      }
    },
    paginatedVenues() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedVenues.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.sortedVenues.length / this.itemsPerPage);
    },
    totalVenues() {
      return this.venues.length;
    },
    activeVenues() {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return this.venues.filter(
        (venue) => venue.lastAppearance && venue.lastAppearance > thirtyDaysAgo
      ).length;
    },
    totalLives() {
      return this.venues.reduce(
        (total, venue) => total + venue.lives.length,
        0
      );
    },
  },
  watch: {
    currentPage() {
      this.$nextTick(() => {
        this.loadImagesForCurrentPage();
      });
    },
    searchQuery() {
      this.$nextTick(() => {
        this.loadImagesForCurrentPage();
      });
    },
    sortBy() {
      this.$nextTick(() => {
        this.loadImagesForCurrentPage();
      });
    },
  },
  mounted() {
    // 관리자 권한 확인
    if (this.userStore.auth !== "admin") {
      this.$router.push("/");
      return;
    }
    this.fetchVenues().then(() => {
      // 초기 페이지 이미지 로드
      this.loadImagesForCurrentPage();
    });
  },
  methods: {
    async fetchVenues() {
      try {
        this.loading = true;
        const db = getFirestore();

        // 모든 컬렉션을 병렬로 가져오기
        const [livesSnapshot, placesResult] = await Promise.allSettled([
          getDocs(collection(db, "lives")),
          getDocs(collection(db, "places")).catch(() => null),
        ]);

        // lives 데이터 처리
        if (livesSnapshot.status !== "fulfilled") {
          throw new Error("라이브 데이터를 가져올 수 없습니다.");
        }

        // places 데이터 처리
        const placesMap = new Map();
        if (placesResult.status === "fulfilled" && placesResult.value) {
          placesResult.value.forEach((doc) => {
            const placeData = doc.data();
            placesMap.set(placeData.name, {
              mapLink: placeData.mapLink || "",
              contact: placeData.contact || "",
              region: placeData.region || "",
              imgFileName: placeData.img || null,
            });
          });
        }

        // venues 맵 생성 (이미지 URL은 지연 로딩)
        const venuesMap = new Map();

        livesSnapshot.value.forEach((doc) => {
          const live = { id: doc.id, ...doc.data() };
          const placeName = live.place || "미지정";

          if (!venuesMap.has(placeName)) {
            const placeInfo = placesMap.get(placeName) || {};
            venuesMap.set(placeName, {
              name: placeName,
              lives: [],
              firstAppearance: null,
              lastAppearance: null,
              mapLink: placeInfo.mapLink || "",
              tempMapLink: placeInfo.mapLink || "",
              venueImage: null, // 지연 로딩으로 변경
              venueImageFileName: placeInfo.imgFileName, // 파일명만 저장
              contact: placeInfo.contact || "",
              tempContact: placeInfo.contact || "",
              savingContact: false,
              editingContact: false,
              region: placeInfo.region || "",
              tempRegion: placeInfo.region || "",
              savingRegion: false,
              editingRegion: false,
              saving: false,
              editing: false,
              imageLoading: false, // 이미지 로딩 상태 추가
              // 공연장명 수정 관련 상태
              tempName: placeName,
              savingName: false,
              editingName: false,
            });
          }

          const venue = venuesMap.get(placeName);
          venue.lives.push(live);

          const liveDate = live.date ? new Date(live.date) : new Date();

          if (!venue.firstAppearance || liveDate < venue.firstAppearance) {
            venue.firstAppearance = liveDate;
          }

          if (!venue.lastAppearance || liveDate > venue.lastAppearance) {
            venue.lastAppearance = liveDate;
          }
        });

        this.venues = Array.from(venuesMap.values());
        this.loading = false;
      } catch (error) {
        console.error("Error fetching venues:", error);
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return "정보 없음";
      return format(date, "yyyy.MM.dd");
    },
    typeIconUrl(type) {
      switch (type) {
        case "vocal":
          return require("@/assets/images/vocal.png");
        case "band":
          return require("@/assets/images/band.png");
        case "dance":
          try {
            return require("@/assets/images/dance.png");
          } catch {
            return require("@/assets/images/mix.png");
          }
        case "mix":
          return require("@/assets/images/mix.png");
        default:
          return require("@/assets/images/mix.png");
      }
    },
    getTypeCount(venue, type) {
      return venue.lives.filter((live) => live.type === type).length;
    },
    getRank(index) {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      return startIndex + index + 1;
    },
    async saveMapLink(venue) {
      if (!venue.tempMapLink || venue.saving) return;

      try {
        venue.saving = true;

        // places collection에 저장 (자동 생성)
        await saveVenueMapLink(venue.name, venue.tempMapLink);

        // 성공 시 venue 객체 업데이트
        venue.mapLink = venue.tempMapLink;
        venue.editing = false;

        this.$alert("지도 링크가 성공적으로 저장되었습니다!");
      } catch (error) {
        console.error("지도 링크 저장 오류:", error);
        this.$alert("지도 링크 저장에 실패했습니다.");
      } finally {
        venue.saving = false;
      }
    },
    startEdit(venue) {
      venue.tempMapLink = venue.mapLink;
      venue.editing = true;
    },
    cancelEdit(venue) {
      venue.tempMapLink = venue.mapLink;
      venue.editing = false;
    },
    goToLiveDetail(liveId) {
      this.$router.push(`/live/${liveId}`);
    },
    handleImageUpdated(venue, url) {
      const venueIndex = this.venues.findIndex((v) => v.name === venue.name);
      if (venueIndex !== -1) {
        this.venues[venueIndex].venueImage = url;
      }
    },
    handleImageRemoved(venue) {
      const venueIndex = this.venues.findIndex((v) => v.name === venue.name);
      if (venueIndex !== -1) {
        this.venues[venueIndex].venueImage = null;
      }
    },
    async saveContact(venue) {
      if (!venue.tempContact || venue.savingContact) return;

      try {
        venue.savingContact = true;

        // places collection에 저장 (자동 생성)
        await saveVenueContact(venue.name, venue.tempContact);

        // 성공 시 venue 객체 업데이트
        venue.contact = venue.tempContact;
        venue.editingContact = false;

        this.$alert("연락처가 성공적으로 저장되었습니다!");
      } catch (error) {
        console.error("연락처 저장 오류:", error);
        this.$alert("연락처 저장에 실패했습니다.");
      } finally {
        venue.savingContact = false;
      }
    },
    startEditContact(venue) {
      venue.tempContact = venue.contact;
      venue.editingContact = true;
    },
    cancelEditContact(venue) {
      venue.tempContact = venue.contact;
      venue.editingContact = false;
    },
    async saveRegion(venue) {
      if (!venue.tempRegion || venue.savingRegion) return;

      try {
        venue.savingRegion = true;

        // places collection에 저장 (자동 생성)
        await saveVenueRegion(venue.name, venue.tempRegion);

        // 성공 시 venue 객체 업데이트
        venue.region = venue.tempRegion;
        venue.editingRegion = false;

        this.$alert("지역 정보가 성공적으로 저장되었습니다!");
      } catch (error) {
        console.error("지역 저장 오류:", error);
        this.$alert("지역 정보 저장에 실패했습니다.");
      } finally {
        venue.savingRegion = false;
      }
    },
    startEditRegion(venue) {
      venue.tempRegion = venue.region;
      venue.editingRegion = true;
    },
    cancelEditRegion(venue) {
      venue.tempRegion = venue.region;
      venue.editingRegion = false;
    },
    async saveVenueName(venue) {
      if (!venue.tempName || venue.savingName || venue.tempName === venue.name) return;

      try {
        venue.savingName = true;

        const oldName = venue.name;
        const newName = venue.tempName;

        // API를 통해 공연장명 변경
        await renameVenue(oldName, newName);

        // 성공 시 venue 객체 업데이트
        venue.name = newName;
        venue.editingName = false;

        this.$alert("공연장명이 성공적으로 변경되었습니다!");
      } catch (error) {
        console.error("공연장명 변경 오류:", error);
        this.$alert("공연장명 변경에 실패했습니다.");
      } finally {
        venue.savingName = false;
      }
    },
    startEditName(venue) {
      venue.tempName = venue.name;
      venue.editingName = true;
    },
    cancelEditName(venue) {
      venue.tempName = venue.name;
      venue.editingName = false;
    },
    async loadVenueImage(venue) {
      if (venue.venueImage || venue.imageLoading || !venue.venueImageFileName) {
        return;
      }

      try {
        venue.imageLoading = true;
        const imageRef = storageRef(
          getStorage(),
          `venue/${venue.venueImageFileName}`
        );
        venue.venueImage = await getDownloadURL(imageRef);
      } catch (error) {
        console.warn(`공연장 이미지 URL 생성 실패 (${venue.name}):`, error);
        venue.venueImage = null;
      } finally {
        venue.imageLoading = false;
      }
    },
    loadImagesForCurrentPage() {
      this.paginatedVenues.forEach((venue) => {
        if (
          venue.venueImageFileName &&
          !venue.venueImage &&
          !venue.imageLoading
        ) {
          this.loadVenueImage(venue);
        }
      });
    },
  },
};
</script>

<style module>
.container {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 15px;
}

.header {
  text-align: center;
  padding: 25px;
  border-radius: 15px;
}

.title {
  font-size: 2rem;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: bold;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

.loading,
.noData {
  text-align: center;
  padding: 50px 15px;
  color: #666;
  font-size: 1rem;
}

.loadingContent {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.spinner {
  width: 35px;
  height: 35px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  color: #333;
  margin-bottom: 15px;
}

.summaryCard {
  background: white;
  text-align: center;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.summaryCard:hover {
  transform: translateY(-2px);
}

.summaryCard h3 {
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #666;
  opacity: 0.9;
  line-height: 1.2;
}

.summaryNumber {
  margin: 0;
  font-size: 1.6rem;
  font-weight: bold;
  color: #333;
  line-height: 1.1;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0px;
  flex-wrap: wrap;
  gap: 12px;
}

.searchBox {
  flex: 1;
  min-width: 200px;
}

.searchInput {
  width: 100%;
  padding: 10px;
  height: 40px !important;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
}

.sortBox {
  min-width: 130px;
}

.sortSelect {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
  background: white;
}

.venueList {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.venueCard {
  background: white;
  padding: 18px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.venueHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.venueRank {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 45px;
}

.rankNumber {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  font-size: 1.1rem;
  font-weight: bold;
}

.venueNameDisplay {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.venueName {
  font-size: 1.3rem;
  color: #333;
  margin: 0;
}

.editNameButton {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.editNameButton:hover {
  opacity: 1;
}

.venueNameEdit {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.venueNameInput {
  flex: 1;
  padding: 7px 10px;
  border: 2px solid #007bff;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: bold;
}

.venueNameActions {
  display: flex;
  gap: 8px;
}

.venueCount {
  background: #007bff;
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
}

.venueInfo {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.infoItem {
  color: #666;
  font-size: 0.9rem;
}

.liveList {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.liveTag {
  background: #f8f9fa;
  color: #333;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.liveTag:hover {
  background: #007bff;
  color: white;
  border-color: #007bff;
  transform: translateY(-1px);
}

.typeIcon {
  width: 11px;
  height: 11px;
  margin-right: 5px;
  vertical-align: middle;
  filter: brightness(0) saturate(100%);
}

.liveTag:hover .typeIcon {
  filter: brightness(0) saturate(100%) invert(100%);
}

.mapLinkSection {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.mapLinkInput {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
}

.mapInput {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
}

.saveButton {
  padding: 7px 14px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.saveButton:hover:not(:disabled) {
  background: #0056b3;
}

.saveButton:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.mapPreview {
  text-align: center;
}

.mapLink {
  display: inline-block;
  color: #007bff;
  text-decoration: none;
  padding: 6px 0px;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.mapLink:hover {
  background: #007bff;
  color: white;
}

.mapLinkDisplay {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.mapLinkText {
  color: #333;
  font-size: 0.9rem;
  flex: 1;
}

.mapLinkText strong {
  color: #666;
  margin-right: 8px;
}

.mapLinkActions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.editButton {
  padding: 5px 10px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.editButton:hover {
  background: #218838;
}

.viewMapButton {
  display: inline-block;
  color: #007bff;
  text-decoration: none;
  padding: 6px 12px;
  border: 1px solid #007bff;
  border-radius: 6px;
  font-size: 0.8rem;
  transition: all 0.2s;
}

.viewMapButton:hover {
  background: #007bff;
  color: white;
}

.editActions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.cancelButton {
  padding: 7px 14px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.cancelButton:hover {
  background: #5a6268;
}

.contactSection {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  padding: 12px;
  background: #fff8e1;
  border-radius: 8px;
  border: 1px solid #ffe0b2;
}

.contactInput {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
}

.contactInputField {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
}

.contactDisplay {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.contactText {
  color: #333;
  font-size: 0.9rem;
  flex: 1;
}

.contactText strong {
  color: #666;
  margin-right: 8px;
}

.contactLink {
  display: inline-block;
  color: #28a745;
  text-decoration: none;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: all 0.2s;
  font-weight: 500;
}

.contactLink:hover {
  background: #28a745;
  color: white;
}

.contactActions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.regionSection {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  padding: 12px;
  background: #e8f5e9;
  border-radius: 8px;
  border: 1px solid #c8e6c9;
}

.regionInput {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
}

.regionInputField {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
}

.regionDisplay {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.regionText {
  color: #333;
  font-size: 0.9rem;
  flex: 1;
}

.regionText strong {
  color: #666;
  margin-right: 8px;
}

.regionValue {
  display: inline-block;
  color: #2e7d32;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
}

.regionActions {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* 페이지네이션 스타일 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 25px;
  gap: 6px;
  flex-wrap: wrap;
}

.pageButton {
  padding: 7px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #ffffff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  text-align: center;
}

.pageButton:hover:not(.activePage):not(:disabled) {
  background: #f8f9fa;
  border-color: #ccc;
  color: #333;
}

.pageButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: #ccc;
  background: #f8f9fa;
}

.activePage {
  background: #222;
  color: #ffffff;
  border-color: #222;
}

.pageInfo {
  font-weight: bold;
  color: #333;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px 10px 10px;
  }

  .header {
    padding: 15px 10px;
  }

  .title {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 0.85rem;
  }

  .summary {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  .summaryCard {
    padding: 10px 6px;
    border-radius: 8px;
  }

  .summaryCard h3 {
    font-size: 0.65rem;
    margin-bottom: 6px;
  }

  .summaryNumber {
    font-size: 1.1rem;
  }

  .controls {
    flex-direction: column;
    padding: 10px 0;
    gap: 10px;
  }

  .searchBox {
    width: 100%;
    min-width: unset;
  }

  .searchInput {
    padding: 8px 10px;
    height: 36px !important;
    font-size: 0.9rem;
    border-radius: 6px;
  }

  .sortBox {
    width: 100%;
    min-width: unset;
  }

  .sortSelect {
    padding: 8px 10px;
    font-size: 0.9rem;
    border-radius: 6px;
  }

  .venueList {
    gap: 12px;
    margin-bottom: 20px;
  }

  .venueCard {
    padding: 12px;
    border-radius: 8px;
  }

  .venueHeader {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }

  .venueRank {
    min-width: 40px;
  }

  .rankNumber {
    width: 32px;
    height: 32px;
    font-size: 0.95rem;
  }

  .venueNameDisplay {
    flex-direction: column;
    align-items: center;
    gap: 5px;
    text-align: center;
  }

  .venueName {
    font-size: 1.1rem;
  }

  .editNameButton {
    font-size: 0.9rem;
    padding: 3px;
  }

  .venueNameEdit {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .venueNameInput {
    width: 100%;
    padding: 6px 10px;
    font-size: 1rem;
  }

  .venueNameActions {
    width: 100%;
    justify-content: center;
  }

  .venueCount {
    padding: 4px 8px;
    font-size: 0.8rem;
  }

  .venueInfo {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    text-align: center;
  }

  .infoItem {
    font-size: 0.85rem;
  }

  .liveList {
    gap: 5px;
    justify-content: center;
  }

  .liveTag {
    padding: 4px 8px;
    font-size: 0.75rem;
    border-radius: 15px;
  }

  .typeIcon {
    width: 10px;
    height: 10px;
    margin-right: 4px;
  }

  .mapLinkSection {
    flex-direction: column;
    margin: 10px 0;
    padding: 10px;
    gap: 8px;
  }

  .mapLinkInput {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .mapInput {
    width: 100%;
    padding: 6px 10px;
    font-size: 0.85rem;
  }

  .saveButton {
    width: 100%;
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .mapLinkDisplay {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .mapLinkText {
    font-size: 0.85rem;
    text-align: center;
    word-break: break-all;
  }

  .mapLinkActions {
    width: 100%;
    justify-content: center;
  }

  .editActions {
    flex-direction: column;
    width: 100%;
    gap: 6px;
  }

  .editButton {
    width: 100%;
    padding: 8px 12px;
    font-size: 0.8rem;
  }

  .cancelButton {
    width: 100%;
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .mapLink {
    font-size: 0.8rem;
    padding: 4px 0;
  }

  .contactSection {
    flex-direction: column;
    margin: 10px 0;
    padding: 10px;
    gap: 8px;
  }

  .contactInput {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .contactInputField {
    width: 100%;
    padding: 6px 10px;
    font-size: 0.85rem;
  }

  .contactDisplay {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .contactText {
    font-size: 0.85rem;
    text-align: center;
  }

  .contactActions {
    width: 100%;
    justify-content: center;
  }

  .contactLink {
    padding: 4px 8px;
    font-size: 0.85rem;
  }

  .regionSection {
    flex-direction: column;
    margin: 10px 0;
    padding: 10px;
    gap: 8px;
  }

  .regionInput {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .regionInputField {
    width: 100%;
    padding: 6px 10px;
    font-size: 0.85rem;
  }

  .regionDisplay {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .regionText {
    font-size: 0.85rem;
    text-align: center;
  }

  .regionActions {
    width: 100%;
    justify-content: center;
  }

  .regionValue {
    padding: 4px 8px;
    font-size: 0.85rem;
  }

  .pagination {
    margin-top: 20px;
    gap: 5px;
  }

  .pageButton {
    padding: 6px 8px;
    font-size: 12px;
    min-width: 28px;
    border-radius: 4px;
  }

  .pageInfo {
    font-size: 0.9rem;
  }

  .loading,
  .noData {
    padding: 40px 10px;
    font-size: 0.95rem;
  }

  .loadingContent {
    gap: 12px;
  }

  .spinner {
    width: 30px;
    height: 30px;
    border-width: 3px;
  }
}
</style>
